/**
 * @lid <-> @c.us JID equivalence.
 *
 * WhatsApp Web emits identities in two interchangeable forms on accounts that
 * have rolled over to linked identities (LID): the LID JID ("…@lid") and the
 * phone-form JID ("…@c.us"). Different code paths store or observe different
 * forms — chat pickers historically stored the phone form, incoming messages and
 * freshly-opened chats can carry the raw LID form — so a direct string compare
 * between the two spuriously fails. That mismatch is the root of both:
 *   - the cancel path's "No active bots relevant for sender" false negative, and
 *   - whatsapp-scheduler#165, where a scheduled message flashed then vanished
 *     from the per-chat sidebar because currentChat.id (@lid) and the stored
 *     recipient id (@c.us) transiently disagreed.
 *
 * `WhatsappContact.phoneNumber._serialized` carries the phone-form resolution
 * once WAWeb's store has fetched it. We look up the given JID in the merged
 * contact map and prefer that field, and also scan the inverse direction so a
 * @c.us input resolves back to a @lid contact. When the mapping isn't loaded yet
 * (first render after a fresh reload) we return just the original JID — better to
 * over-match than to silently drop.
 *
 * Returns the set of equivalent JIDs (as an array) so callers can match against
 * either form. The first element is the original input JID.
 *
 * PURE: no store/thunk/extension dependency — shared by the cancel path
 * (WhatsappThunks) and the display selector (UserSelectors) so both apply the
 * one equivalence rule.
 */
import { getSerialized, type SerializableKey } from '@extension/wa-communication-layer/core/utils/wa-key-serialization';

/** A contact's phone-form JID, tolerant of WA Web having dropped the raw `_serialized` getter. */
const phoneJidOf = (contact: { phoneNumber?: SerializableKey & { id?: SerializableKey } } | undefined): string | undefined =>
    getSerialized(contact?.phoneNumber) ?? getSerialized(contact?.phoneNumber?.id);

export const resolveSenderJidEquivalents = (
    senderId: string,
    mergedContactMap: Record<string, any> | undefined,
): string[] => {
    const out = new Set<string>();
    out.add(senderId);

    const contact = mergedContactMap?.[senderId];
    const phoneJid = phoneJidOf(contact);
    if (phoneJid && phoneJid !== senderId) out.add(phoneJid);

    // Also try the inverse: if we got a @c.us sender but the user
    // configured the bot via a @lid contact, scan the map for a contact
    // whose phoneNumber matches our senderId.
    if (senderId.endsWith('@c.us') && mergedContactMap) {
        for (const key of Object.keys(mergedContactMap)) {
            if (!key.endsWith('@lid')) continue;
            const c = mergedContactMap[key];
            const pn = phoneJidOf(c);
            if (pn === senderId) {
                out.add(key);
                break;
            }
        }
    }

    return Array.from(out);
};
